import React, { useState, useMemo } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import { SearchInput, SummaryStrip, ListLoader, ListEmpty, ListCard, thClass } from "../../stock/StockUI";
import { formatCurrency, MarginBadge, SortTh, tdClass, ErrorLine } from "./technicalFilesUI";

const FoodCostDashboard = ({ isDarkMode }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'margin_percent', direction: 'desc' });

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchFoodCost = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            let url = '/recipes/food-cost';

            if (debouncedSearchTerm) {
                url += `?search=${encodeURIComponent(debouncedSearchTerm)}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || "Erreur lors de la récupération des données");
        }
    };

    const { data: foodCostData, isLoading, error } = useQuery({
        queryKey: ['food-cost', debouncedSearchTerm],
        queryFn: fetchFoodCost,
        keepPreviousData: true
    });

    // Global stats — the backend already computes avg_cost/avg_margin correctly
    // (excluding recipes with no linked product from the margin average, see
    // RecipeController::getFoodCost() summary block) — reuse it rather than
    // recomputing client-side and silently treating "no margin" as a 0% margin.
    const stats = useMemo(() => {
        if (!foodCostData?.data) return {
            avgCost: 0,
            avgMargin: null,
            unprofitable: 0,
            profitable: 0
        };

        const recipes = foodCostData.data;
        const unprofitable = recipes.filter(r => r.margin_percent != null && r.margin_percent < 15).length;
        const profitable = recipes.filter(r => r.margin_percent != null && r.margin_percent >= 30).length;

        return {
            avgCost: foodCostData.summary?.avg_cost ?? 0,
            avgMargin: foodCostData.summary?.avg_margin ?? null,
            unprofitable,
            profitable
        };
    }, [foodCostData]);

    // Sort recipes
    const sortedRecipes = useMemo(() => {
        if (!foodCostData?.data) return [];
        const sortable = [...foodCostData.data];
        sortable.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
            if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sortable;
    }, [foodCostData, sortConfig]);

    const requestSort = (key) => {
        setSortConfig({
            key,
            direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
        });
    };

    return (
        <div className="text-sm">
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher une recette..." />
            </div>

            {isLoading ? (
                <ListLoader label="Chargement du food cost..." />
            ) : error ? (
                <ErrorLine message={error.message} />
            ) : (<>
                {/* KPIs : bande à filets, comme le Stock */}
                <SummaryStrip items={[
                    { key: 'cost', label: 'Coût moyen', value: `${formatCurrency(stats.avgCost)} DH`, color: 'text-primary-dark dark:text-primary' },
                    { key: 'margin', label: 'Marge moyenne', value: stats.avgMargin != null ? `${formatCurrency(stats.avgMargin)}%` : '—' },
                    { key: 'unprofitable', label: 'Non rentables', value: stats.unprofitable, hint: 'Marge < 15 %', color: 'text-red-600 dark:text-red-400' },
                    { key: 'profitable', label: 'Très rentables', value: stats.profitable, hint: 'Marge ≥ 30 %', color: 'text-green-600 dark:text-green-400' },
                ]} />

                <ListCard>
                    {sortedRecipes.length === 0 ? (
                        <ListEmpty icon={TrendingUp} title="Aucune recette"
                                   text={debouncedSearchTerm ? "Aucune recette ne correspond à votre recherche." : "Aucune fiche active à analyser."} />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <SortTh label="Recette" sortKey="recipe_name" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortTh label="Coût HT" sortKey="cost_price_ht" sortConfig={sortConfig} onSort={requestSort} align="right" />
                                    <SortTh label="Prix vente HT" sortKey="sale_price_ht" sortConfig={sortConfig} onSort={requestSort} align="right" />
                                    <SortTh label="Marge" sortKey="margin_percent" sortConfig={sortConfig} onSort={requestSort} align="right" />
                                    <th className={`${thClass} text-center`}>Rentable</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedRecipes.map((recipe) => (
                                    <tr key={recipe.recipe_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className={`${tdClass} font-semibold text-gray-900 dark:text-gray-100`}>{recipe.recipe_name}</td>
                                        <td className={`${tdClass} text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100`}>
                                            {formatCurrency(recipe.cost_price_ht)} DH
                                        </td>
                                        <td className={`${tdClass} text-right tabular-nums text-gray-700 dark:text-gray-300`}>
                                            {recipe.sale_price_ht != null ? `${formatCurrency(recipe.sale_price_ht)} DH` : '—'}
                                        </td>
                                        <td className={`${tdClass} text-right`}>
                                            {recipe.margin_percent != null
                                                ? <MarginBadge value={recipe.margin_percent} />
                                                : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className={`${tdClass} text-center`}>
                                            {recipe.margin_percent == null ? (
                                                <span className="text-gray-400">—</span>
                                            ) : recipe.margin_percent >= 30 ? (
                                                <TrendingUp className="w-5 h-5 inline text-green-600 dark:text-green-400" />
                                            ) : recipe.margin_percent >= 15 ? (
                                                <TrendingUp className="w-5 h-5 inline text-amber-500" />
                                            ) : (
                                                <TrendingDown className="w-5 h-5 inline text-red-600 dark:text-red-400" />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListCard>
            </>)}
        </div>
    );
};

export default FoodCostDashboard;
